import React from 'react'
import { Pressable, Text, View } from 'react-native'

import { colors } from '../theme'
import { body3, headlineFont, label1 } from '../theme/typography'
import { Icon, symbolName } from './ui/Icon'
import { Spinner } from './ui/Spinner'

export interface ExerciseCardProps {
  title: string
  subtitle?: string
  icon?: React.ReactNode
  onPress: () => void
  disabled?: boolean
  isFeatured?: boolean
  isCompleted?: boolean
  rightIcon?: React.ReactNode
  isFavorite?: boolean
  isFavoriteLoading?: boolean
  onFavoritePress?: () => void
}

// oxlint-disable-next-line react-doctor/no-many-boolean-props -- shared card API already used across many callers
export const ExerciseCard = ({
  title,
  subtitle,
  icon,
  onPress,
  disabled = false,
  isFeatured = false,
  isCompleted = false,
  rightIcon,
  isFavorite = false,
  isFavoriteLoading = false,
  onFavoritePress,
}: ExerciseCardProps) => {
  const hasSubtitle = subtitle != null && subtitle.trim().length > 0

  return (
    <Pressable
      className={`bg-background-secondary py-4.5 px-4 rounded-lg flex-row ${hasSubtitle ? 'items-start' : 'items-center'} border border-gray-50 min-h-20 ${disabled ? 'opacity-60' : ''}`}
      onPress={onPress}
      disabled={disabled}
      style={{
        marginBottom: 8,
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
      }}
    >
      {icon != null ? (
        <View className="w-8 h-8 justify-center items-center mr-3 self-center">
          {icon}
        </View>
      ) : null}
      <View className="flex-1">
        <View className="flex-row items-center">
          <Text
            style={[
              label1,
              {
                fontFamily: headlineFont,
                fontWeight: 'bold',
                color: colors.textBody,
                flex: 1,
                marginRight: 8,
              },
            ]}
            numberOfLines={1}
          >
            {title}
          </Text>
        </View>
        {hasSubtitle && (
          <Text
            style={[body3, { color: colors.textBody }]}
            numberOfLines={2}
            ellipsizeMode="tail"
          >
            {subtitle}
          </Text>
        )}
      </View>
      <View className="ml-2 flex-row items-center self-center">
        {isCompleted && (
          <View
            className="h-5 w-5 items-center justify-center rounded-full"
            style={{ backgroundColor: colors.primary }}
          >
            <Icon
              name={symbolName('checkmark', 'check')}
              size={12}
              color="#FFFFFF"
            />
          </View>
        )}
        {rightIcon ?? (
          <Pressable
            onPress={onFavoritePress}
            disabled={disabled || isFavoriteLoading}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            {isFavoriteLoading ? (
              <Spinner size="small" />
            ) : (
              <Icon
                name={
                  isFavorite
                    ? symbolName('star.fill', 'star')
                    : symbolName('star', 'star')
                }
                size={24}
                color={isFavorite ? 'accent' : 'muted'}
              />
            )}
          </Pressable>
        )}
      </View>
    </Pressable>
  )
}
